/**
 * Parr theme JS v2 (mf-theme.js) — owner: theme-core. ES module, loaded from layout/theme.liquid (defer).
 * Only touches `[data-mf-*]` hooks and the markup they own. Public API on `window.MF`.
 * Contract: theme-work/v2/CSS-API.md (v1 contract kept: MF.openCart / MF.addToCart({openDrawer}) / data-mf-cart-open /
 * mf:cart:update / MF.scrollTop / MF.onScroll / MF.bp keys — some values changed on purpose, see CSS-API v2 §2).
 *
 * Cart events — Horizon 4.2.0 (verified in theme-work/full/assets, not guessed):
 *   `StandardEvents.cartLinesUpdate` = 'shopify:cart:lines-update' (package `@shopify/events`, importmap snippets/scripts.liquid:14,
 *   name in assets/standard-events.d.ts:739). product-form.js:439-455 dispatches `new CartLinesUpdateEvent({ action:'add',
 *   context:'product', lines:[{merchandiseId, quantity}], promise })` and resolves it (product-form.js:546-556) with
 *   `{ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(/cart.js), detail: { items, source, sourceId, itemCount, productId,
 *   sections, didError } }`; cart-drawer.js:85 reads `event.action`. We dispatch the same shape (source 'mf-theme') and listen to it
 *   (action 'add' from any other source → refresh + open the mf cart drawer).
 *   With settings.cart_type = 'page' Horizon renders no cart drawer (snippets/cart-drawer.liquid:11) and product-form.js:324
 *   always preventDefaults, so add-to-cart never leaves the page.
 */
let SE = null;
try { SE = await import('@shopify/events'); } catch (e) { /* CDN blocked → plain events only */ }

const CART_EVENT = (SE && SE.StandardEvents && SE.StandardEvents.cartLinesUpdate) || 'shopify:cart:lines-update';
const CART_ERROR_EVENT = (SE && SE.StandardEvents && SE.StandardEvents.cartError) || 'shopify:cart:error';
const routes = () => (window.Theme && window.Theme.routes) || {};
const rootUrl = () => ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/');
const $ = (sel, root = document) => root.querySelector(sel);
const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
const on = (type, sel, fn, opts) => document.addEventListener(type, (e) => { const t = e.target instanceof Element ? e.target.closest(sel) : null; if (t) fn(e, t); }, opts);
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const debounce = (fn, ms) => { let t = 0; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };
const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
// Horizon 4.2.0 scrolls .page-wrapper (not the window) from 990px up — base.css:27-55. Read both.
const pageWrapper = () => document.querySelector('.page-wrapper');
const scrollTop = () => { const pw = pageWrapper(); return Math.max(window.scrollY || 0, pw ? pw.scrollTop : 0); };
const onScroll = (fn) => { window.addEventListener('scroll', fn, { passive: true }); const pw = pageWrapper(); if (pw) pw.addEventListener('scroll', fn, { passive: true }); };

const MF = (window.MF = window.MF || {});
MF.version = '2.0.0';
MF.scrollTop = scrollTop; MF.onScroll = onScroll;
/** Breakpoints (DESIGN-SPEC v2 §0.6). v1 keys kept, values changed on purpose: mobile ≤768, tablet ≤1023, nav (hamburger) ≤1023.
 *  sm/md/lg/xl are min-widths. */
MF.bp = { mobile: 768, tablet: 1023, nav: 1023, sm: 600, md: 769, lg: 1024, xl: 1280 };
const mq = (q) => window.matchMedia(q).matches;
MF.mq = {
  mobile: () => mq(`(max-width: ${MF.bp.mobile}px)`),
  tablet: () => mq(`(max-width: ${MF.bp.tablet}px)`),
  navMobile: () => mq(`(max-width: ${MF.bp.nav}px)`),
  up: (name) => mq(`(min-width: ${MF.bp[name]}px)`),
};
const isDesktopNav = () => !MF.mq.navMobile();
MF.events = {
  ready: 'mf:ready',
  cartUpdate: 'mf:cart:update',           // detail: { cart } raw /cart.js|/cart/change.js json, or { cart: null, normalized } when relayed from Horizon
  cartAdd: 'mf:cart:add',                 // detail: { items, cart }
  cartError: 'mf:cart:error',             // detail: { message, response }
  cartOpen: 'mf:cart:open',               // detail: {}
  cartClose: 'mf:cart:close',
  cartRender: 'mf:cart:render',           // detail: { root } — drawer body re-rendered
  quickviewOpen: 'mf:quickview:open',     // detail: { handle, root }
  quickviewClose: 'mf:quickview:close',
  offcanvasToggle: 'mf:offcanvas:toggle', // detail: { id, open }
  searchToggle: 'mf:search:toggle',       // detail: { open }
  carouselChange: 'mf:carousel:change',   // detail: { index, root }
  accordionToggle: 'mf:accordion:toggle', // detail: { open }
  loadMore: 'mf:loadmore',                // detail: { items, url, target }
  filterUpdate: 'mf:filter:update',       // detail: { url, form }
  filterToggle: 'mf:filter:toggle',       // detail: { open }
  recentUpdate: 'mf:recent:update',       // detail: { handles }
  domUpdate: 'mf:dom:update',             // detail: { roots }
  horizonCartLinesUpdate: CART_EVENT,
  horizonCartError: CART_ERROR_EVENT,
};
const emit = (name, detail = {}, target = document) => target.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));
document.documentElement.classList.add('mf-js');

/* ---------------- helpers: section id, section fetch, image url ---------------- */
/** Section id of the section that contains `el` (data-section-id wins, else the .shopify-section wrapper id). */
function sectionId(el) {
  if (!el) return '';
  const tagged = el.closest('[data-section-id]');
  if (tagged && tagged.dataset.sectionId) return tagged.dataset.sectionId;
  const wrap = el.closest('.shopify-section');
  return wrap ? wrap.id.replace(/^shopify-section-/, '') : '';
}
/** GET `url` rendered as one section (Section Rendering API) → Document. */
async function fetchSection(url, id, opts = {}) {
  const u = new URL(url, location.origin);
  if (id) u.searchParams.set('section_id', id);
  const res = await fetch(u.toString(), { headers: { Accept: 'text/html' }, signal: opts.signal });
  if (!res.ok) throw new Error(`section ${id} ${res.status}`);
  return new DOMParser().parseFromString(await res.text(), 'text/html');
}
function imgUrl(src, width) {
  if (!src) return '';
  try { const u = new URL(String(src).startsWith('//') ? location.protocol + src : src, location.origin); u.searchParams.set('width', String(width)); return u.toString(); } catch (e) { return src; }
}
MF.sectionId = sectionId; MF.fetchSection = fetchSection;

/* ---------------- money ---------------- */
const moneyFormat = () => { const el = $('[data-mf-money-format]'); return (el && el.getAttribute('data-mf-money-format')) || '${{amount}}'; };
function formatMoney(cents, format = moneyFormat()) {
  const n = Number(cents) / 100;
  const fixed = (d, thousands = ',', decimal = '.') => { const p = n.toFixed(d).split('.'); p[0] = p[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands); return p.join(decimal); };
  const map = {
    amount: fixed(2), amount_no_decimals: fixed(0),
    amount_with_comma_separator: fixed(2, '.', ','), amount_no_decimals_with_comma_separator: fixed(0, '.', ','),
    amount_with_space_separator: fixed(2, ' ', ','), amount_no_decimals_with_space_separator: fixed(0, ' ', ','),
    amount_with_apostrophe_separator: fixed(2, "'", '.'),
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => (map[k] !== undefined ? map[k] : map.amount)).replace(/<[^>]+>/g, '');
}
MF.formatMoney = formatMoney;

/* ---------------- scroll lock + focus trap + overlay ---------------- */
const locks = new Set();
// ≥990 Horizon scrolls .page-wrapper (base.css:27-55) → lock it with Horizon's own html[scroll-lock] (base.css:37).
// <990 the WINDOW scrolls: html[scroll-lock] would make .page-wrapper `overflow:hidden`, i.e. a new scroll container,
// and the sticky header (child of .page-wrapper) jumps to its in-flow position off-screen (AD P3 review P0). There we
// lock with body{overflow:hidden} only — it propagates to the viewport, .page-wrapper stays non-scrolling, sticky holds.
const wrapperScrollsMq = window.matchMedia('(min-width: 990px)');
function syncLock() {
  const any = locks.size > 0;
  const html = document.documentElement;
  document.body.classList.toggle('mf-scroll-lock', any);
  const useHtml = any && wrapperScrollsMq.matches;
  if (useHtml && !html.hasAttribute('scroll-lock')) { html.setAttribute('scroll-lock', ''); html.dataset.mfScrollLock = '1'; }
  else if (!useHtml && html.dataset.mfScrollLock) { html.removeAttribute('scroll-lock'); delete html.dataset.mfScrollLock; } // only release what we set
}
wrapperScrollsMq.addEventListener('change', syncLock);
const lockScroll = (owner) => { locks.add(owner); syncLock(); };
const unlockScroll = (owner) => { locks.delete(owner); syncLock(); };
MF.lockScroll = lockScroll; MF.unlockScroll = unlockScroll;

const FOCUSABLE = 'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';
// Real tab stops only: skip tabindex="-1" (e.g. card image link a.mf-card__media) and anything aria-hidden / inert / hidden
// (AD P4 review #4: after load more focus landed on the aria-hidden image link instead of the card title).
const focusables = (root) => $$(FOCUSABLE, root).filter((el) => el.getAttribute('tabindex') !== '-1'
  && !el.closest('[inert], [hidden], [aria-hidden="true"]') && el.getClientRects().length > 0);
const traps = [];
const pushTrap = (el) => { if (!traps.includes(el)) traps.push(el); };
const popTrap = (el) => { const i = traps.indexOf(el); if (i > -1) traps.splice(i, 1); };
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Tab' || !traps.length) return;
  const root = traps[traps.length - 1];
  const f = focusables(root);
  if (!f.length) { e.preventDefault(); root.focus({ preventScroll: true }); return; }
  const first = f[0]; const last = f[f.length - 1]; const active = document.activeElement;
  if (e.shiftKey && (active === first || !root.contains(active))) { e.preventDefault(); last.focus(); }
  else if (!e.shiftKey && (active === last || !root.contains(active))) { e.preventDefault(); first.focus(); }
});
MF.trapFocus = pushTrap; MF.releaseFocus = popTrap;

let overlay = null;
function ensureOverlay() {
  if (overlay && overlay.isConnected) return overlay;
  overlay = document.createElement('div');
  overlay.className = 'mf-overlay';
  overlay.setAttribute('data-mf-overlay', '');
  overlay.addEventListener('click', () => { closeOffcanvas(); closeModal(); });
  document.body.appendChild(overlay);
  return overlay;
}
function syncOverlay() {
  const needs = openStack.some((el) => !el.hasAttribute('data-mf-offcanvas-no-overlay')) || !!$('.mf-modal.is-open');
  ensureOverlay().classList.toggle('is-open', needs);
}

/* ---------------- cart state (header badge, totals) ---------------- */
function updateCartUI({ count, totalCents }) {
  if (Number.isFinite(count)) {
    $$('[data-mf-cart-count]').forEach((el) => { el.textContent = count > 0 ? String(count) : ''; el.classList.toggle('is-empty', !count); });
    $$('[data-mf-cart-live]').forEach((el) => { el.textContent = `${el.dataset.mfCartLive || 'Cart'}: ${count}`; });
  }
  if (Number.isFinite(totalCents)) $$('[data-mf-cart-total]').forEach((el) => { el.textContent = formatMoney(totalCents); });
}
async function fetchCart() {
  const r = await fetch(`${routes().cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } });
  return r.json();
}
async function refreshCart() {
  const cart = await fetchCart();
  updateCartUI({ count: cart.item_count, totalCents: cart.total_price });
  emit(MF.events.cartUpdate, { cart });
  return cart;
}
MF.refreshCart = refreshCart;

/* ---------------- cart drawer (sections/mf-cart-drawer.liquid) ---------------- */
const cartRoot = () => $('[data-mf-cart-drawer]');
const cartPanel = () => { const r = cartRoot(); return r && $('[data-mf-cart-panel]', r); };
const cartSectionId = () => { const r = cartRoot(); return (r && r.dataset.sectionId) || ''; };
let cartOpener = null;
let noteOpen = false;

function renderCartSection(html) {
  const root = cartRoot();
  if (!root || !html) return false;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const fresh = $('[data-mf-cart-drawer]', doc);
  const body = $('[data-mf-cart-body]', root);
  const nb = fresh && $('[data-mf-cart-body]', fresh);
  if (!body || !nb) return false;
  const active = document.activeElement;
  const focusKey = active && root.contains(active) ? active.getAttribute('data-mf-focus') : null;
  const hadFocus = active && root.contains(active);
  body.replaceWith(nb);
  // state lives in data-mf-cart-state-* (NOT data-mf-cart-count/-total: those are the badge/total text hooks updateCartUI writes into)
  ['mfCartStateCount', 'mfCartStateTotal'].forEach((k) => { if (fresh.dataset[k] !== undefined) root.dataset[k] = fresh.dataset[k]; });
  updateCartUI({ count: Number(fresh.dataset.mfCartStateCount), totalCents: Number(fresh.dataset.mfCartStateTotal) });
  if (noteOpen) toggleNote(true, false);
  if (hadFocus && root.classList.contains('is-open')) {
    const target = (focusKey && $(`[data-mf-focus="${CSS.escape(focusKey)}"]`, root)) || cartPanel();
    if (target) target.focus({ preventScroll: true });
  }
  if (root.classList.contains('is-open')) initUpsell(root);   // recommendations only fetched while the drawer is open
  emit(MF.events.cartRender, { root });
  return true;
}
async function fetchCartSection() {
  const id = cartSectionId();
  if (!id) return false;
  // no Accept: application/json — on /products/<handle> Shopify then answers with the product JSON instead of the sections
  const res = await fetch(`${location.pathname}?sections=${encodeURIComponent(id)}`);
  const json = await res.json();
  return renderCartSection(json && json[id]);
}
MF.renderCart = fetchCartSection;

function openCart(opts = {}) {
  const root = cartRoot();
  if (!root) { location.href = routes().cart_url || '/cart'; return false; }
  if (root.classList.contains('is-open')) return true;
  closeSearch(); closeOffcanvas(); closeModal();
  cartOpener = opts.opener || (document.activeElement instanceof HTMLElement ? document.activeElement : null);
  root.removeAttribute('inert');
  root.setAttribute('aria-hidden', 'false');
  root.classList.add('is-open');
  lockScroll('cart');
  const panel = cartPanel();
  if (panel) { pushTrap(panel); panel.focus({ preventScroll: true }); }
  initUpsell(root);
  $$('[data-mf-cart-open]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
  emit(MF.events.cartOpen, {}, root);
  return true;
}
function closeCart() {
  const root = cartRoot();
  if (!root || !root.classList.contains('is-open')) return;
  root.classList.remove('is-open');
  root.setAttribute('aria-hidden', 'true');
  root.setAttribute('inert', '');
  unlockScroll('cart');
  const panel = cartPanel(); if (panel) popTrap(panel);
  $$('[data-mf-cart-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
  const back = (cartOpener && cartOpener.isConnected && cartOpener.getClientRects().length) ? cartOpener : $('[data-mf-cart-open]');
  if (back) back.focus({ preventScroll: true });
  cartOpener = null;
  emit(MF.events.cartClose, {}, root);
}
MF.openCart = openCart; MF.closeCart = closeCart;
on('click', '[data-mf-cart-open]', (e, btn) => {
  if (!cartRoot() || e.metaKey || e.ctrlKey || e.shiftKey) return; // no drawer (cart template / no JS section) → plain link to /cart
  e.preventDefault();
  openCart({ opener: btn });
});
on('click', '[data-mf-cart-close]', (e) => { e.preventDefault(); closeCart(); });

function horizonDeferred(target, payload) {
  if (!(SE && SE.CartLinesUpdateEvent)) return null;
  const deferred = SE.CartLinesUpdateEvent.createPromise();
  target.dispatchEvent(new SE.CartLinesUpdateEvent({ ...payload, promise: deferred.promise }));
  return deferred;
}
const resolveHorizon = (deferred, cart, detail) => {
  if (!deferred) return;
  deferred.resolve({ cart: cart && SE ? SE.CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : null, detail: { source: 'mf-theme', ...detail } });
};

/** Change one line of the cart from the drawer: /cart/change.js + Section Rendering of the drawer (id read at runtime). */
async function changeLine(key, quantity, lineEl) {
  const id = cartSectionId();
  const errEl = lineEl && $('[data-mf-cart-line-error]', lineEl);
  if (lineEl) { lineEl.classList.add('mf-is-busy'); lineEl.setAttribute('aria-busy', 'true'); }
  const deferred = horizonDeferred(lineEl || document, { action: quantity === 0 ? 'remove' : 'update', context: 'cart', lines: [{ id: String(key), quantity }] });
  try {
    const res = await fetch(`${routes().cart_change_url || '/cart/change'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity, sections: id ? [id] : undefined, sections_url: location.pathname }),
    });
    const json = await res.json();
    if (!res.ok || json.status) {
      const message = json.description || json.message || 'Could not update the cart';
      if (errEl) { errEl.textContent = message; errEl.hidden = false; }
      if (lineEl) { lineEl.classList.remove('mf-is-busy'); lineEl.removeAttribute('aria-busy'); }
      resolveHorizon(deferred, null, { didError: true });
      emit(MF.events.cartError, { message, response: json });
      return { ok: false, message };
    }
    const rendered = json.sections && id ? renderCartSection(json.sections[id]) : false;
    if (!rendered) await fetchCartSection().catch(() => {});
    updateCartUI({ count: json.item_count, totalCents: json.total_price });
    resolveHorizon(deferred, json, { didError: false, items: json.items, sections: json.sections });
    emit(MF.events.cartUpdate, { cart: json });
    return { ok: true, cart: json };
  } catch (err) {
    if (deferred) deferred.reject(err);
    location.href = routes().cart_url || '/cart';
    return { ok: false, message: String(err) };
  }
}
MF.changeCartLine = changeLine;
on('click', '[data-mf-cart-qty]', (e, btn) => {
  e.preventDefault();
  const line = btn.closest('[data-mf-cart-line]');
  const input = line && $('[data-mf-cart-qty-input]', line);
  if (!line || !input || line.classList.contains('mf-is-busy')) return;
  const next = Math.max(0, (Number(input.value) || 0) + (Number(btn.getAttribute('data-mf-cart-qty')) || 0));
  input.value = String(next);
  changeLine(line.dataset.mfCartLine, next, line);
});
on('change', '[data-mf-cart-qty-input]', (e, input) => {
  const line = input.closest('[data-mf-cart-line]');
  if (line) changeLine(line.dataset.mfCartLine, Math.max(0, Number(input.value) || 0), line);
});
on('click', '[data-mf-cart-remove]', (e, btn) => {
  e.preventDefault();
  const line = btn.closest('[data-mf-cart-line]');
  if (line && !line.classList.contains('mf-is-busy')) changeLine(line.dataset.mfCartLine, 0, line);
});
// order note: toggle + save (debounced) to /cart/update.js
function toggleNote(force, focus = true) {
  const root = cartRoot(); if (!root) return;
  const box = $('[data-mf-cart-note]', root); const btn = $('[data-mf-cart-note-toggle]', root);
  if (!box) return;
  noteOpen = force !== undefined ? force : box.hidden;
  box.hidden = !noteOpen;
  if (btn) btn.setAttribute('aria-expanded', String(noteOpen));
  if (noteOpen && focus) { const ta = $('textarea', box); if (ta) ta.focus(); }
}
on('click', '[data-mf-cart-note-toggle]', (e) => { e.preventDefault(); toggleNote(); });
const saveNote = debounce((value) => {
  fetch(`${routes().cart_update_url || '/cart/update'}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ note: value }) }).catch(() => {});
}, 500);
on('input', '[data-mf-cart-note] textarea', (e, ta) => saveNote(ta.value));

// upsell: /recommendations/products.json (real data); empty → section stays hidden. Markup owned by core (.mf-upsell-card).
const upsellCache = new Map();
async function initUpsell(root = cartRoot()) {
  const box = root && $('[data-mf-cart-upsell]', root);
  if (!box || box.dataset.mfUpsellReady) return;
  box.dataset.mfUpsellReady = '1';
  const pid = box.dataset.productId;
  const limit = Number(box.dataset.limit) || 4;
  const exclude = new Set((box.dataset.exclude || '').split(',').filter(Boolean).map(String));
  if (!pid) return;
  try {
    let products = upsellCache.get(pid);
    if (!products) {
      const res = await fetch(`${rootUrl()}recommendations/products.json?product_id=${encodeURIComponent(pid)}&limit=${limit + exclude.size}&intent=related`, { headers: { Accept: 'application/json' } });
      products = res.ok ? ((await res.json()).products || []) : [];
      upsellCache.set(pid, products);
    }
    const list = products.filter((p) => p.available && !exclude.has(String(p.id))).slice(0, limit);
    const track = $('[data-mf-carousel-track]', box);
    if (!list.length || !track) return;
    const addLabel = box.dataset.addLabel || 'Add';
    track.innerHTML = list.map((p) => {
      const v = (p.variants || []).find((x) => x.available) || (p.variants || [])[0];
      const src = p.featured_image || (p.images && p.images[0]) || '';
      const img = src ? `<img src="${esc(imgUrl(src, 128))}" srcset="${esc(imgUrl(src, 64))} 64w, ${esc(imgUrl(src, 128))} 128w, ${esc(imgUrl(src, 192))} 192w" sizes="64px" alt="" width="64" height="96" loading="lazy">` : '';
      return `<li class="mf-upsell-card"><a class="mf-upsell-card__media" href="${esc(p.url)}" tabindex="-1" aria-hidden="true">${img}</a>`
        + `<div class="mf-upsell-card__info"><a class="mf-upsell-card__title" href="${esc(p.url)}">${esc(p.title)}</a><span class="mf-upsell-card__price">${esc(formatMoney(p.price))}</span></div>`
        + (v ? `<button type="button" class="mf-btn mf-btn--secondary mf-upsell-card__add" data-mf-add-to-cart="${esc(v.id)}" aria-label="${esc(addLabel)}: ${esc(p.title)}">${esc(addLabel)}</button>` : '')
        + '</li>';
    }).join('');
    box.hidden = false;
    initCarousels(box);
    if (box.mfCarousel) box.mfCarousel.update();
  } catch (err) { /* no upsell */ }
}

/* ---------------- add to cart ---------------- */
const cartItemsSectionIds = () => $$('cart-items-component[data-section-id]').map((el) => el.dataset.sectionId).filter(Boolean);
function showFormError(source, message) {
  const root = source instanceof HTMLFormElement ? source : null;
  const el = root ? (root.querySelector('[data-mf-add-error]') || (root.parentElement && root.parentElement.querySelector('[data-mf-add-error]'))) : null;
  if (el) { el.textContent = message; el.hidden = !message; } else if (message) console.warn('[mf-theme] add to cart:', message);
}
/**
 * @param {HTMLFormElement|{id:string|number, quantity?:number, properties?:object, selling_plan?:string}} source
 * @param {{openDrawer?:boolean, button?:HTMLElement}} opts
 */
async function addToCart(source, opts = {}) {
  const isForm = source instanceof HTMLFormElement;
  const fd = isForm ? new FormData(source) : new FormData();
  if (!isForm) {
    fd.set('id', String(source.id));
    fd.set('quantity', String(source.quantity || 1));
    if (source.selling_plan) fd.set('selling_plan', String(source.selling_plan));
    if (source.properties) for (const [k, v] of Object.entries(source.properties)) fd.set(`properties[${k}]`, String(v));
  }
  if (!fd.get('quantity')) fd.set('quantity', '1');
  const drawerId = cartSectionId();
  const sections = [...cartItemsSectionIds(), drawerId].filter(Boolean);
  if (sections.length) { fd.set('sections', sections.join(',')); fd.set('sections_url', location.pathname); }
  const quantity = Number(fd.get('quantity')) || 1;
  const merchandiseId = String(fd.get('id') || '');
  const button = opts.button;
  if (button) { button.classList.add('is-loading'); button.setAttribute('aria-busy', 'true'); }
  const target = isForm ? source : (button || document);
  const deferred = horizonDeferred(target, { action: 'add', context: 'product', lines: [{ merchandiseId, quantity }] });
  const done = () => { if (button && button.isConnected) { button.classList.remove('is-loading'); button.removeAttribute('aria-busy'); } };
  let res; let json;
  try {
    res = await fetch(routes().cart_add_url || '/cart/add.js', { method: 'POST', body: fd, headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } });
    json = await res.json();
  } catch (err) {
    // the add request itself failed (network) → fall back to a normal form post / cart page. Never after a successful add.
    if (deferred) deferred.reject(err);
    done();
    if (isForm) { source.removeAttribute('data-mf-add-to-cart'); source.submit(); } else { location.href = routes().cart_url || '/cart'; }
    return { ok: false, message: String(err) };
  }
  if (!res.ok || json.status) {
    const message = json.description || json.message || 'Could not add to cart';
    if (SE && SE.CartErrorEvent) target.dispatchEvent(new SE.CartErrorEvent({ error: json.message || message, code: 'INVALID', detail: { description: json.description, errors: json.errors } }));
    const cart = await refreshCart().catch(() => null);
    resolveHorizon(deferred, cart, { didError: true, itemCount: quantity, items: cart ? cart.items : [] });
    emit(MF.events.cartError, { message, response: json }, target);
    showFormError(source, message);
    done();
    return { ok: false, message, response: json };
  }
  // added: from here on only UI refresh — failures here must not re-submit (would add the item twice)
  const items = json.items || [json];
  let cart = null;
  try {
    const rendered = drawerId && json.sections ? renderCartSection(json.sections[drawerId]) : false;
    if (drawerId && !rendered) await fetchCartSection().catch(() => {});
    cart = await refreshCart().catch(() => null);
  } catch (err) { console.warn('[mf-theme] cart refresh after add failed', err); }
  resolveHorizon(deferred, cart, { didError: false, itemCount: quantity, sections: json.sections, items: cart ? cart.items : items, productId: items[0] && items[0].product_id });
  emit(MF.events.cartAdd, { items, cart }, target);
  showFormError(source, '');
  closeModal();
  done();
  if (opts.openDrawer !== false) openCart({ opener: button || (isForm ? source.querySelector('[type="submit"]') : null) });
  return { ok: true, items, cart };
}
MF.addToCart = addToCart;
document.addEventListener('submit', (e) => {
  const form = e.target instanceof Element ? e.target.closest('form[data-mf-add-to-cart]') : null;
  if (!form) return;
  e.preventDefault();
  addToCart(form, { button: form.querySelector('[type="submit"]') || undefined });
});
on('click', 'button[data-mf-add-to-cart], a[data-mf-add-to-cart]', (e, btn) => {
  if (btn.closest('form[data-mf-add-to-cart]')) return;
  const id = btn.getAttribute('data-mf-add-to-cart') || btn.dataset.variantId;
  if (!id) return;
  e.preventDefault();
  const scope = btn.closest('[data-mf-qty-scope]');
  const qtyInput = scope ? scope.querySelector('input[name="quantity"]') : null;
  addToCart({ id, quantity: Number(qtyInput && qtyInput.value) || Number(btn.dataset.quantity) || 1 }, { button: btn });
});

// Cart changes made by Horizon (product-form, cart page…): keep badge + drawer in sync; action 'add' → open the drawer.
document.addEventListener(CART_EVENT, (event) => {
  const action = event.action;
  if (!event.promise) { refreshCart().catch(() => {}); fetchCartSection().catch(() => {}); return; }
  event.promise.then(({ cart, detail } = {}) => {
    if (detail && detail.source === 'mf-theme') return; // our own dispatch — already handled
    fetchCartSection().catch(() => {});
    if (cart) {
      const amount = Number(cart.cost && cart.cost.totalAmount && cart.cost.totalAmount.amount);
      updateCartUI({ count: cart.totalQuantity || 0, totalCents: Number.isFinite(amount) ? Math.round(amount * 100) : undefined });
      emit(MF.events.cartUpdate, { cart: null, normalized: cart });
    } else refreshCart().catch(() => {});
    if (action === 'add' && !(detail && detail.didError)) openCart();
  }).catch(() => {});
});
window.addEventListener('pageshow', (e) => { if (e.persisted) { refreshCart().catch(() => {}); fetchCartSection().catch(() => {}); } });

/* ---------------- quantity ± (generic) ---------------- */
on('click', '[data-mf-qty-plus], [data-mf-qty-minus]', (e, btn) => {
  e.preventDefault();
  const scope = btn.closest('[data-mf-qty]') || btn.closest('[data-mf-qty-scope]') || btn.parentElement;
  const input = scope && scope.querySelector('input[type="number"], input[name="quantity"], input[name="updates[]"]');
  if (!input) return;
  const step = Number(input.step) || 1;
  const min = input.min !== '' ? Number(input.min) : 1;
  const max = input.max !== '' ? Number(input.max) : Infinity;
  const v = Math.min(max, Math.max(min, (Number(input.value) || 0) + (btn.hasAttribute('data-mf-qty-plus') ? step : -step)));
  input.value = String(v);
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('change', { bubbles: true }));
});

/* ---------------- off-canvas (mobile menu, filter drawer) ---------------- */
const openStack = [];
let offcanvasOpener = null;
function setHeaderBottom() {
  const h = $('.mf-header-section');
  const bottom = h ? Math.max(0, Math.round(h.getBoundingClientRect().bottom)) : 0;
  document.documentElement.style.setProperty('--mf-header-bottom', `${bottom}px`);
  return bottom;
}
function openOffcanvas(id, opener) {
  const el = document.getElementById(id);
  if (!el) return false;
  closeSearch();
  if (el.hasAttribute('data-mf-offcanvas-below-header')) { const b = setHeaderBottom(); el.classList.toggle('is-detached', b < 10); }
  offcanvasOpener = opener || (document.activeElement instanceof HTMLElement ? document.activeElement : null);
  el.classList.add('is-open');
  el.setAttribute('aria-hidden', 'false');
  el.removeAttribute('inert');
  if (!openStack.includes(el)) openStack.push(el);
  syncOverlay();
  lockScroll(`oc:${id}`);
  pushTrap(el.hasAttribute('data-mf-offcanvas-below-header') ? (el.closest('.shopify-section') || el) : el);
  const focusTarget = focusables(el)[0] || el;
  if (typeof focusTarget.focus === 'function') focusTarget.focus({ preventScroll: true });
  $$(`[data-mf-offcanvas-open="${id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
  emit(MF.events.offcanvasToggle, { id, open: true }, el);
  return true;
}
function closeOffcanvas(id) {
  const targets = id ? [document.getElementById(id)].filter(Boolean) : openStack.slice();
  let closed = 0;
  targets.forEach((el) => {
    if (!el.classList.contains('is-open')) return;
    closed++;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    el.setAttribute('inert', '');
    const i = openStack.indexOf(el); if (i > -1) openStack.splice(i, 1);
    popTrap(el); popTrap(el.closest('.shopify-section') || el);
    unlockScroll(`oc:${el.id}`);
    $$(`[data-mf-offcanvas-open="${el.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
    $$('[data-mf-submenu-panel]', el).forEach((p) => { p.hidden = true; });
    $$('[data-mf-submenu-main]', el).forEach((m) => { m.hidden = false; });
    emit(MF.events.offcanvasToggle, { id: el.id, open: false }, el);
  });
  syncOverlay();
  if (closed && offcanvasOpener && offcanvasOpener.isConnected && typeof offcanvasOpener.focus === 'function') { offcanvasOpener.focus({ preventScroll: true }); offcanvasOpener = null; }
}
MF.openOffcanvas = openOffcanvas; MF.closeOffcanvas = closeOffcanvas;
on('click', '[data-mf-offcanvas-open]', (e, btn) => { e.preventDefault(); const id = btn.getAttribute('data-mf-offcanvas-open'); const el = document.getElementById(id); if (el && el.classList.contains('is-open')) closeOffcanvas(id); else openOffcanvas(id, btn); });
on('click', '[data-mf-offcanvas-close]', (e, btn) => { e.preventDefault(); const oc = btn.closest('[data-mf-offcanvas]'); closeOffcanvas(btn.getAttribute('data-mf-offcanvas-close') || (oc && oc.id) || undefined); });
// mobile menu sub-panels: a child panel replaces the main list; "‹ Back" returns
on('click', '[data-mf-submenu-open]', (e, btn) => {
  e.preventDefault();
  const panel = document.getElementById(btn.getAttribute('data-mf-submenu-open'));
  const menu = btn.closest('[data-mf-offcanvas]');
  if (!panel || !menu) return;
  $$('[data-mf-submenu-main]', menu).forEach((m) => { m.hidden = true; });
  panel.hidden = false;
  panel.dataset.mfOpener = btn.id || '';
  const back = $('[data-mf-submenu-back]', panel); if (back) back.focus({ preventScroll: true });
  menu.scrollTop = 0;
});
on('click', '[data-mf-submenu-back]', (e, btn) => {
  e.preventDefault();
  const panel = btn.closest('[data-mf-submenu-panel]');
  const menu = btn.closest('[data-mf-offcanvas]');
  if (!panel || !menu) return;
  panel.hidden = true;
  $$('[data-mf-submenu-main]', menu).forEach((m) => { m.hidden = false; });
  const opener = panel.dataset.mfOpener && document.getElementById(panel.dataset.mfOpener);
  if (opener) opener.focus({ preventScroll: true });
});

/* ---------------- search: desktop inline dropdown + mobile panel (/search/suggest.json) ---------------- */
const searchPanel = () => $('[data-mf-search-panel]');
function openSearch() {
  if (isDesktopNav()) {
    const input = $('[data-mf-search="inline"] input[type="search"]');
    if (input) { input.focus(); return true; }
  }
  const panel = searchPanel();
  if (!panel) { location.href = routes().search_url || '/search'; return false; }
  closeOffcanvas();
  panel.classList.add('is-open');
  panel.setAttribute('aria-hidden', 'false');
  panel.removeAttribute('inert');
  $$('[data-mf-search-open]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
  const input = $('input[type="search"]', panel);
  if (input) setTimeout(() => input.focus({ preventScroll: true }), reducedMotion() ? 0 : 50);
  emit(MF.events.searchToggle, { open: true }, panel);
  return true;
}
function closeSearch() {
  $$('[data-mf-search].is-active').forEach((s) => s.classList.remove('is-active'));
  const panel = searchPanel();
  if (!panel || !panel.classList.contains('is-open')) return;
  const hadFocus = panel.contains(document.activeElement);
  panel.classList.remove('is-open');
  panel.setAttribute('aria-hidden', 'true');
  panel.setAttribute('inert', '');
  $$('[data-mf-search-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
  if (hadFocus) { const b = $('[data-mf-search-open]'); if (b) b.focus({ preventScroll: true }); }
  emit(MF.events.searchToggle, { open: false }, panel);
}
MF.openSearch = openSearch; MF.closeSearch = closeSearch;
on('click', '[data-mf-search-open]', (e) => { e.preventDefault(); const p = searchPanel(); if (p && p.classList.contains('is-open')) closeSearch(); else openSearch(); });
on('click', '[data-mf-search-close]', (e) => { e.preventDefault(); closeSearch(); });

const suggestCtrls = new WeakMap();
const runSuggest = debounce(async (input) => {
  const root = input.closest('[data-mf-search]');
  const out = root && $('[data-mf-search-results]', root);
  const form = input.closest('form');
  if (!out) return;
  const q = input.value.trim();
  if (q.length < 2) { out.innerHTML = ''; root.classList.remove('is-active'); return; }
  const prev = suggestCtrls.get(root); if (prev) prev.abort();
  const ctrl = new AbortController(); suggestCtrls.set(root, ctrl);
  if (form) form.classList.add('is-loading');
  try {
    const base = routes().predictive_search_url || '/search/suggest';
    const res = await fetch(`${base}.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last`, { signal: ctrl.signal, headers: { Accept: 'application/json' } });
    const json = await res.json();
    const products = (json.resources && json.resources.results && json.resources.results.products) || [];
    const allLabel = out.dataset.mfAllLabel || 'View all results';
    const emptyLabel = out.dataset.mfEmptyLabel || 'No products found';
    const searchUrl = `${routes().search_url || '/search'}?q=${encodeURIComponent(q)}&type=product&options%5Bprefix%5D=last`;
    out.innerHTML = products.length
      ? `<ul class="mf-search__list" role="list">${products.map((p) => {
          const src = (p.featured_image && p.featured_image.url) || p.image || '';
          const price = Math.round(Number(p.price) * 100);
          return `<li><a class="mf-search__result" href="${esc(p.url)}">${src ? `<img class="mf-search__thumb" src="${esc(imgUrl(src, 96))}" srcset="${esc(imgUrl(src, 48))} 48w, ${esc(imgUrl(src, 96))} 96w, ${esc(imgUrl(src, 144))} 144w" sizes="48px" alt="" width="48" height="72" loading="lazy">` : '<span class="mf-search__thumb" aria-hidden="true"></span>'}<span class="mf-search__result-info"><span class="mf-search__result-title">${esc(p.title)}</span><span class="mf-search__result-price">${esc(formatMoney(price))}</span></span></a></li>`;
        }).join('')}</ul><a class="mf-search__all mf-link-underline" href="${esc(searchUrl)}">${esc(allLabel)}</a>`
      : `<p class="mf-search__empty">${esc(emptyLabel)}</p>`;
    if (root.contains(document.activeElement)) root.classList.add('is-active');
  } catch (err) { if (err && err.name !== 'AbortError') { out.innerHTML = ''; root.classList.remove('is-active'); } }
  finally { if (form && suggestCtrls.get(root) === ctrl) form.classList.remove('is-loading'); }
}, 250);
on('input', '[data-mf-search] input[type="search"]', (e, input) => runSuggest(input));
document.addEventListener('focusin', (e) => {
  $$('[data-mf-search].is-active').forEach((s) => { if (!s.contains(e.target)) s.classList.remove('is-active'); });
  const root = e.target instanceof Element ? e.target.closest('[data-mf-search]') : null;
  const out = root && $('[data-mf-search-results]', root);
  if (out && out.innerHTML.trim()) root.classList.add('is-active');
});
document.addEventListener('pointerdown', (e) => {
  $$('[data-mf-search].is-active').forEach((s) => { if (!s.contains(e.target)) s.classList.remove('is-active'); });
});

/* ---------------- modal / quick view (kept from v1; not used by v2 cards) ---------------- */
function ensureModal() {
  let modal = $('#mf-quickview-modal');
  if (modal) return modal;
  modal = document.createElement('div');
  modal.id = 'mf-quickview-modal';
  modal.className = 'mf-modal';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-hidden', 'true');
  modal.innerHTML = '<div class="mf-modal__backdrop" data-mf-modal-close></div><div class="mf-modal__dialog"><button type="button" class="mf-modal__close" data-mf-modal-close aria-label="Close"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M5.5 5.5l13 13M18.5 5.5l-13 13"/></svg></button><div class="mf-modal__body" data-mf-modal-body></div></div>';
  document.body.appendChild(modal);
  return modal;
}
function closeModal() {
  const modal = $('#mf-quickview-modal');
  if (!modal || !modal.classList.contains('is-open')) return;
  modal.classList.remove('is-open');
  modal.setAttribute('aria-hidden', 'true');
  popTrap(modal); unlockScroll('modal'); syncOverlay();
  emit(MF.events.quickviewClose, {}, modal);
}
const qvCache = new Map();
async function openQuickView(handle, sid = 'mf-quickview') {
  const modal = ensureModal();
  const body = modal.querySelector('[data-mf-modal-body]');
  modal.classList.add('is-open', 'is-loading');
  modal.setAttribute('aria-hidden', 'false');
  lockScroll('modal'); pushTrap(modal);
  try {
    let html = qvCache.get(handle);
    if (!html) {
      const res = await fetch(`${rootUrl()}products/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(sid)}`, { headers: { Accept: 'text/html' } });
      if (!res.ok) throw new Error(`quick view ${res.status}`);
      html = await res.text();
      qvCache.set(handle, html);
    }
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const inner = doc.querySelector('.shopify-section') || doc.body;
    body.innerHTML = inner.innerHTML;
    body.querySelectorAll('script').forEach((old) => { const s = document.createElement('script'); Array.from(old.attributes).forEach((a) => s.setAttribute(a.name, a.value)); s.textContent = old.textContent; old.replaceWith(s); });
    initCarousels(body); initHoverImages(body);
    emit(MF.events.quickviewOpen, { handle, root: body }, modal);
    const closeBtn = modal.querySelector('.mf-modal__close'); if (closeBtn) closeBtn.focus({ preventScroll: true });
  } catch (err) {
    location.href = `${rootUrl()}products/${handle}`;
  } finally {
    modal.classList.remove('is-loading');
  }
}
MF.openQuickView = openQuickView; MF.closeQuickView = closeModal;
on('click', '[data-mf-quickview]', (e, btn) => { const h = btn.getAttribute('data-mf-quickview'); if (!h) return; e.preventDefault(); openQuickView(h, btn.getAttribute('data-mf-quickview-section') || 'mf-quickview'); });
on('click', '[data-mf-modal-close]', (e) => { e.preventDefault(); closeModal(); });

/* ---------------- Escape: close the top-most layer ---------------- */
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape') return;
  const cart = cartRoot();
  if (cart && cart.classList.contains('is-open')) { closeCart(); return; }
  if ($('.mf-modal.is-open')) { closeModal(); return; }
  if (openStack.length) { closeOffcanvas(openStack[openStack.length - 1].id); return; }
  const panel = searchPanel();
  if (panel && panel.classList.contains('is-open')) { closeSearch(); return; }
  if ($('[data-mf-search].is-active')) { closeSearch(); return; }
  const openNav = $('.mf-nav__item.is-open');
  if (openNav) { closeNavItem(openNav, true); return; }
  const hovered = e.target instanceof Element ? e.target.closest('.mf-nav__item') : null;
  if (hovered && hovered.querySelector('.mf-nav__sub')) closeNavItem(hovered, true);
});

/* ---------------- carousel (scroll-snap, 1 card per step, progress, static when it fits) ---------------- */
const boundCarousels = new WeakSet();
const setDisabled = (btn, d) => { if (!btn) return; btn.setAttribute('aria-disabled', d ? 'true' : 'false'); btn.classList.toggle('is-disabled', d); };
function initCarousels(root = document) {
  const cars = $$('[data-mf-carousel]', root);
  if (root instanceof Element && root.matches('[data-mf-carousel]')) cars.unshift(root);
  cars.forEach((car) => {
    if (boundCarousels.has(car)) return; // WeakSet, not an attribute: a morph can strip attributes but keeps listeners
    const track = car.querySelector('[data-mf-carousel-track]');
    if (!track) return;
    boundCarousels.add(car);
    const prev = car.querySelector('[data-mf-carousel-prev]');
    const next = car.querySelector('[data-mf-carousel-next]');
    const progress = car.querySelector('[data-mf-carousel-progress]');
    const loop = car.hasAttribute('data-mf-carousel-loop');
    const step = () => { const first = track.firstElementChild; const cs = getComputedStyle(track); const gap = parseFloat(cs.columnGap || cs.gap) || 0; return first ? first.getBoundingClientRect().width + gap : track.clientWidth; };
    const behavior = () => (reducedMotion() ? 'auto' : 'smooth');
    const update = () => {
      car.classList.remove('is-static');
      const max = track.scrollWidth - track.clientWidth;
      const overflow = max > 2;
      car.classList.toggle('has-overflow', overflow);
      car.classList.toggle('is-static', !overflow);   // items ≤ visible columns → static centred grid, no arrows/progress
      setDisabled(prev, !overflow || (!loop && track.scrollLeft <= 2));
      setDisabled(next, !overflow || (!loop && track.scrollLeft >= max - 2));
      if (progress) {
        const size = overflow ? Math.min(1, track.clientWidth / track.scrollWidth) : 1;
        const pos = overflow ? Math.min(1, Math.max(0, track.scrollLeft / max)) : 0;
        progress.style.setProperty('--mf-progress-size', size.toFixed(4));
        progress.style.setProperty('--mf-progress-pos', pos.toFixed(4));
      }
      const idx = Math.round(track.scrollLeft / Math.max(1, step()));
      if (car.dataset.mfIndex !== String(idx)) { car.dataset.mfIndex = String(idx); emit(MF.events.carouselChange, { index: idx, root: car }, car); }
      car.querySelectorAll('[data-mf-carousel-dot]').forEach((d, i) => d.classList.toggle('is-active', i === idx));
    };
    const go = (dir) => {
      const max = track.scrollWidth - track.clientWidth;
      if (loop && dir > 0 && track.scrollLeft >= max - 2) { track.scrollTo({ left: 0, behavior: behavior() }); return; }
      if (loop && dir < 0 && track.scrollLeft <= 2) { track.scrollTo({ left: max, behavior: behavior() }); return; }
      track.scrollBy({ left: dir * step() * (Number(car.dataset.mfCarouselStep) || 1), behavior: behavior() });
    };
    const click = (dir) => (e) => { e.preventDefault(); const b = e.currentTarget; if (b.getAttribute('aria-disabled') === 'true') return; go(dir); };
    if (prev) prev.addEventListener('click', click(-1));
    if (next) next.addEventListener('click', click(1));
    car.querySelectorAll('[data-mf-carousel-dot]').forEach((d, i) => d.addEventListener('click', () => track.scrollTo({ left: i * step(), behavior: behavior() })));
    let raf = 0;
    track.addEventListener('scroll', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(update); }, { passive: true });
    new ResizeObserver(() => update()).observe(track);
    new MutationObserver(() => update()).observe(track, { childList: true });
    const auto = Number(car.dataset.mfCarouselAutoplay);
    if (auto > 0 && !reducedMotion()) {
      let timer = setInterval(() => go(1), auto);
      const stop = () => clearInterval(timer);
      const start = () => { stop(); timer = setInterval(() => go(1), auto); };
      car.addEventListener('pointerenter', stop); car.addEventListener('pointerleave', start);
      car.addEventListener('focusin', stop); car.addEventListener('focusout', start);
      document.addEventListener('visibilitychange', () => (document.hidden ? stop() : start()));
    }
    car.mfCarousel = { go, update, next: () => go(1), prev: () => go(-1), goTo: (i) => track.scrollTo({ left: i * step(), behavior: behavior() }) };
    update();
  });
}
MF.initCarousels = initCarousels;

/* ---------------- hover image (lazy 2nd image) + lazy fade-in ---------------- */
const boundHover = new WeakSet();
/** Idempotent: each wrapper is bound once (WeakSet); load() reads data-src live, so a morph that swaps the
 *  2nd image (or strips its src) is picked up on the next hover. Also accepts the wrapper itself as root. */
function initHoverImages(root = document) {
  const wraps = $$('[data-mf-hover-img]', root);
  if (root instanceof Element && root.matches('[data-mf-hover-img]')) wraps.unshift(root);
  wraps.forEach((wrap) => {
    if (boundHover.has(wrap)) return;
    boundHover.add(wrap);
    const load = () => {
      const alt = wrap.querySelector('.mf-hover-img__alt, img[data-src]');
      const src = alt && alt.getAttribute('data-src');
      if (!src || alt.getAttribute('src') === src) return;
      alt.setAttribute('src', src);
      const ss = alt.getAttribute('data-srcset'); if (ss) alt.setAttribute('srcset', ss);
    };
    wrap.addEventListener('pointerenter', load, { passive: true });
    wrap.addEventListener('focusin', load);
    const card = wrap.closest('[data-mf-card], .mf-card');
    if (card && card !== wrap) card.addEventListener('focusin', load);
  });
}
MF.initHoverImages = initHoverImages;
function initFadeImages(root = document) {
  const imgs = $$('img.mf-fade-in:not(.is-loaded)', root);
  if (root instanceof HTMLImageElement && root.matches('img.mf-fade-in')) imgs.push(root);
  imgs.forEach((img) => {
    const done = () => img.classList.add('is-loaded');
    if (img.complete && img.naturalWidth) done();
    else { img.addEventListener('load', done, { once: true }); img.addEventListener('error', done, { once: true }); }
  });
}
/* Broken product image at runtime → same empty state as "no image" in Liquid (CSS-API §6): the parent .mf-media gets
 * .mf-media--empty (bag icon 32 #cecdcc on #f9f9f9) and the broken <img> gets .is-broken (hidden). A broken hover image
 * (.mf-hover-img__alt) is only hidden — the main image stays. Capture phase: `error` does not bubble. */
function markBroken(img) {
  if (!(img instanceof HTMLImageElement) || img.classList.contains('is-broken')) return;
  img.classList.add('is-broken', 'is-loaded');
  if (img.classList.contains('mf-hover-img__alt')) return;
  const media = img.closest('.mf-media');
  if (media) media.classList.add('mf-media--empty');
}
document.addEventListener('error', (e) => { if (e.target instanceof HTMLImageElement && e.target.closest('.mf-media')) markBroken(e.target); }, true);
function scanBroken(root = document) {
  // images that failed before this module ran (complete + 0 natural size + a real src)
  $$('.mf-media img', root).forEach((img) => { if (img.complete && img.naturalWidth === 0 && (img.currentSrc || img.getAttribute('src'))) markBroken(img); });
}
MF.markBrokenImage = markBroken;

/* ---------------- accordion: <details data-mf-accordion> animates height (--mf-ease-accordion, --mf-dur) ---------------- */
const cssVar = (name, fallback) => (getComputedStyle(document.documentElement).getPropertyValue(name) || '').trim() || fallback;
const durMs = (v) => (/ms$/.test(v) ? parseFloat(v) : parseFloat(v) * 1000) || 300;
function setDetails(details, open, animate = !reducedMotion()) {
  const summary = details.querySelector(':scope > summary');
  if (!summary || details.dataset.mfAnimating) return;
  if (open === details.open) return;
  if (!animate) { details.open = open; emit(MF.events.accordionToggle, { open }, details); return; }
  const start = details.offsetHeight;
  details.dataset.mfAnimating = '1';
  details.style.overflow = 'hidden';
  if (open) details.open = true;
  const end = open ? details.scrollHeight : summary.offsetHeight + (parseFloat(getComputedStyle(details).borderTopWidth) || 0) + (parseFloat(getComputedStyle(details).borderBottomWidth) || 0);
  const anim = details.animate({ height: [`${start}px`, `${end}px`] }, { duration: durMs(cssVar('--mf-dur', '.3s')), easing: cssVar('--mf-ease-accordion', 'ease') });
  const finish = () => { if (!open) details.open = false; details.style.overflow = ''; delete details.dataset.mfAnimating; emit(MF.events.accordionToggle, { open }, details); };
  anim.onfinish = finish; anim.oncancel = finish;
}
MF.setAccordion = setDetails;
on('click', 'details[data-mf-accordion] > summary', (e, summary) => {
  const details = summary.parentElement;
  if (details.hasAttribute('data-mf-accordion-desktop-open') && MF.mq.up('md')) { e.preventDefault(); return; }
  e.preventDefault();
  setDetails(details, !details.open);
});
// footer menus: open + static on ≥769, accordion (closed) on ≤768
function syncDesktopAccordions() {
  const desktop = MF.mq.up('md');
  $$('details[data-mf-accordion-desktop-open]').forEach((d) => {
    const s = d.querySelector(':scope > summary');
    if (desktop) { d.open = true; if (s) s.setAttribute('tabindex', '-1'); }
    else { if (s) s.removeAttribute('tabindex'); if (!d.dataset.mfAccInit) d.open = false; }
    d.dataset.mfAccInit = '1';
  });
}

/* ---------------- announcement rotator: hold 6.5s, slide .5s, pause on hover/focus; reduced motion → ‹ › buttons ---------------- */
function initRotators(root = document) {
  $$('[data-mf-rotator]', root).forEach((rot) => {
    if (rot.dataset.mfRotatorReady) return;
    const track = $('[data-mf-rotator-track]', rot);
    const items = track ? Array.from(track.children) : [];
    if (items.length < 2) return;
    rot.dataset.mfRotatorReady = '1';
    rot.classList.add('has-multiple');
    const interval = Number(rot.dataset.mfRotatorInterval) || 6500;
    let index = 0; let timer = 0; let paused = false;
    const show = (i) => {
      index = (i + items.length) % items.length;
      track.style.transform = `translateX(${-100 * index}%)`;
      items.forEach((it, n) => { const hidden = n !== index; it.setAttribute('aria-hidden', String(hidden)); if (hidden) it.setAttribute('inert', ''); else it.removeAttribute('inert'); });
    };
    const stop = () => { clearInterval(timer); timer = 0; };
    const start = () => { stop(); if (!paused && !reducedMotion() && !document.hidden) timer = setInterval(() => show(index + 1), interval + 500); };
    rot.addEventListener('pointerenter', () => { paused = true; stop(); });
    rot.addEventListener('pointerleave', () => { paused = false; start(); });
    rot.addEventListener('focusin', () => { paused = true; stop(); });
    rot.addEventListener('focusout', (e) => { if (!rot.contains(e.relatedTarget)) { paused = false; start(); } });
    document.addEventListener('visibilitychange', () => (document.hidden ? stop() : start()));
    const prev = $('[data-mf-rotator-prev]', rot); const next = $('[data-mf-rotator-next]', rot);
    if (prev) prev.addEventListener('click', () => show(index - 1));
    if (next) next.addEventListener('click', () => show(index + 1));
    show(0); start();
  });
}

/* ---------------- recently viewed: localStorage `mf:rv` (handles, newest first, max 8) ---------------- */
MF.recent = {
  key: 'mf:rv',
  max: 8,
  get({ exclude } = {}) {
    let list = [];
    try { list = JSON.parse(localStorage.getItem(this.key) || '[]'); } catch (e) { list = []; }
    if (!Array.isArray(list)) list = [];
    const ex = [].concat(exclude || []);
    return list.filter((h) => typeof h === 'string' && h && !ex.includes(h)).slice(0, this.max);
  },
  add(handle) {
    if (!handle) return this.get();
    const list = [handle, ...this.get({ exclude: handle })].slice(0, this.max);
    try { localStorage.setItem(this.key, JSON.stringify(list)); } catch (e) { /* storage full / private mode */ }
    emit(MF.events.recentUpdate, { handles: list });
    return list;
  },
  clear() { try { localStorage.removeItem(this.key); } catch (e) { /* noop */ } emit(MF.events.recentUpdate, { handles: [] }); },
};
function trackRecent(root = document) { $$('[data-mf-recent-add]', root).forEach((el) => { if (!el.dataset.mfRecentDone) { el.dataset.mfRecentDone = '1'; MF.recent.add(el.getAttribute('data-mf-recent-add')); } }); }

/* ---------------- load more (collection): Section Rendering of the next page, append items ---------------- */
async function loadMore(btn) {
  const url = btn.getAttribute('href') || btn.dataset.mfLoadMoreUrl;
  if (!url || btn.classList.contains('is-loading')) return;
  const block = btn.closest('[data-mf-load-more-root]');
  const targetSel = btn.dataset.mfLoadMoreTarget || '[data-mf-load-more-items]';
  const scope = btn.closest('.shopify-section') || document;
  const target = $(targetSel, scope) || $(targetSel);
  const id = btn.dataset.mfLoadMoreSection || sectionId(btn);
  if (!target || !id) { location.href = url; return; }
  btn.classList.add('is-loading'); btn.setAttribute('aria-busy', 'true');
  try {
    const doc = await fetchSection(url, id);
    const fresh = $(targetSel, doc);
    const items = fresh ? Array.from(fresh.children) : [];
    items.forEach((n) => target.appendChild(n));
    const freshBlock = $('[data-mf-load-more-root]', doc);
    if (block) { if (freshBlock) block.replaceWith(freshBlock); else block.remove(); }
    const clean = new URL(url, location.origin); clean.searchParams.delete('section_id');
    history.replaceState(history.state, '', clean.pathname + clean.search + clean.hash);
    const first = items[0];
    if (first) {
      const f = focusables(first)[0];
      if (f) f.focus({ preventScroll: false });
      else { first.setAttribute('tabindex', '-1'); first.focus(); }
    }
    applyFilterSizes();
    emit(MF.events.loadMore, { items, url: clean.toString(), target }, target);
  } catch (err) {
    location.href = url; // fallback: plain ?page= navigation
  }
}
MF.loadMore = loadMore;
on('click', '[data-mf-load-more]', (e, btn) => { if (e.metaKey || e.ctrlKey) return; e.preventDefault(); loadMore(btn); });

/* ---------------- filters (collection): sidebar toggle (sessionStorage), AJAX form, active-filter links ---------------- */
const FILTER_KEY = 'mf:filter-open';
const filterLayout = () => $('[data-mf-filter-layout]');
const filterOpen = () => { const l = filterLayout(); return !!(l && l.classList.contains('is-filter-open')); };
function applyFilterSizes(root = document) {
  // cards may tag images with data-mf-sizes-filter="<sizes when the sidebar is open>" (CARD-API: grid-filter)
  const open = filterOpen();
  $$('img[data-mf-sizes-filter]', root).forEach((img) => {
    if (!img.dataset.mfSizesGrid) img.dataset.mfSizesGrid = img.getAttribute('sizes') || '';
    const want = open ? img.dataset.mfSizesFilter : img.dataset.mfSizesGrid;
    if (want && img.getAttribute('sizes') !== want) img.setAttribute('sizes', want);
  });
}
function setFilterOpen(open, persist = true) {
  const layout = filterLayout();
  if (!layout) return;
  layout.classList.toggle('is-filter-open', open);
  $$('[data-mf-filter-toggle]').forEach((b) => b.setAttribute('aria-expanded', String(open)));
  const sidebar = $$('[data-mf-filter-toggle]').map((b) => document.getElementById(b.getAttribute('data-mf-filter-toggle'))).find(Boolean);
  if (sidebar) { if (open) { sidebar.removeAttribute('inert'); sidebar.removeAttribute('aria-hidden'); } else { sidebar.setAttribute('inert', ''); sidebar.setAttribute('aria-hidden', 'true'); } }
  if (persist) { try { sessionStorage.setItem(FILTER_KEY, open ? '1' : '0'); } catch (e) { /* noop */ } }
  applyFilterSizes();
  emit(MF.events.filterToggle, { open }, layout);
}
MF.filters = { isOpen: filterOpen, setOpen: setFilterOpen, apply: (url, form) => applyFilters(url, form) };
on('click', '[data-mf-filter-toggle]', (e) => { e.preventDefault(); setFilterOpen(!filterOpen()); });
function restoreFilterState() {
  if (!filterLayout()) return;
  let saved = '0'; try { saved = sessionStorage.getItem(FILTER_KEY) || '0'; } catch (e) { /* noop */ }
  setFilterOpen(MF.mq.up('md') && saved === '1', false);
}
let filterCtrl = null;
async function applyFilters(url, form) {
  const host = form || $('form[data-mf-filter-form]');
  const id = (host && (host.dataset.mfFilterSection || sectionId(host))) || '';
  const updateSel = ((host && host.dataset.mfFilterUpdate) || '[data-mf-filter-results]').split(',').map((s) => s.trim()).filter(Boolean);
  const regions = updateSel.flatMap((s) => $$(s));
  if (!id) { location.href = url; return; }
  if (filterCtrl) filterCtrl.abort();
  filterCtrl = new AbortController();
  regions.forEach((r) => { r.classList.add('mf-is-busy'); r.setAttribute('aria-busy', 'true'); });
  try {
    const doc = await fetchSection(url, id, { signal: filterCtrl.signal });
    updateSel.forEach((s) => {
      const olds = $$(s); const news = $$(s, doc);
      olds.forEach((old, i) => {
        const nu = news[i];
        if (!nu) return;
        if (old.contains(document.activeElement) && old.matches('form, form *') && document.activeElement.matches('input[type="text"], input[type="number"]')) return; // do not yank a field being typed in
        old.replaceWith(nu);
      });
    });
    const counts = $$('[data-mf-filter-count]', doc);
    $$('[data-mf-filter-count]').forEach((el, i) => { if (counts[i]) el.textContent = counts[i].textContent; });
    const clean = new URL(url, location.origin); clean.searchParams.delete('section_id');
    history.replaceState(history.state, '', clean.pathname + clean.search);
    applyFilterSizes();
    emit(MF.events.filterUpdate, { url: clean.toString(), form: host });
  } catch (err) {
    if (err && err.name === 'AbortError') return;
    location.href = url;
  } finally {
    $$(updateSel.join(',')).forEach((r) => { r.classList.remove('mf-is-busy'); r.removeAttribute('aria-busy'); });
  }
}
const filterUrl = (form) => {
  const params = new URLSearchParams();
  for (const [k, v] of new FormData(form)) if (String(v).trim() !== '') params.append(k, String(v));
  const action = form.getAttribute('action') || location.pathname;
  const qs = params.toString();
  return action.split('?')[0] + (qs ? `?${qs}` : '');
};
const filterDebounced = debounce((form) => applyFilters(filterUrl(form), form), 500);
document.addEventListener('change', (e) => {
  const el = e.target;
  const form = el instanceof Element ? (el.form && el.form.matches('[data-mf-filter-form]') ? el.form : el.closest('form[data-mf-filter-form]')) : null;
  if (!form) return;
  if (el.matches('input[type="text"], input[type="number"], input[type="range"]')) filterDebounced(form);
  else applyFilters(filterUrl(form), form);
});
on('input', 'form[data-mf-filter-form] input[type="range"], form[data-mf-filter-form] input[type="number"]', (e, el) => { if (el.form) filterDebounced(el.form); });
document.addEventListener('submit', (e) => {
  const form = e.target instanceof Element ? e.target.closest('form[data-mf-filter-form]') : null;
  if (!form) return;
  e.preventDefault();
  applyFilters(filterUrl(form), form);
  if (form.hasAttribute('data-mf-filter-close-on-submit')) closeOffcanvas();
});
on('click', 'a[data-mf-filter-link]', (e, a) => { if (e.metaKey || e.ctrlKey) return; e.preventDefault(); applyFilters(a.href, $('form[data-mf-filter-form]')); });

/* ---------------- header: heights, sticky compact|always|none, nav fit, dropdown keyboard ---------------- */
function closeNavItem(item, focusLink) {
  item.classList.remove('is-open'); item.classList.add('is-closed');
  const link = item.querySelector(':scope > .mf-nav__link');
  if (link) { link.setAttribute('aria-expanded', 'false'); if (focusLink) link.focus(); }
}
function initNav(header) {
  $$('.mf-nav__item', header).forEach((item) => {
    if (!item.querySelector('.mf-nav__sub') || item.dataset.mfNavReady) return;
    item.dataset.mfNavReady = '1';
    const link = item.querySelector(':scope > .mf-nav__link');
    const set = (open) => { item.classList.toggle('is-open', open); if (open) item.classList.remove('is-closed'); if (link) link.setAttribute('aria-expanded', String(open)); };
    item.addEventListener('pointerenter', () => { item.classList.remove('is-closed'); if (link) link.setAttribute('aria-expanded', 'true'); });
    item.addEventListener('pointerleave', () => { if (!item.classList.contains('is-open') && link) link.setAttribute('aria-expanded', 'false'); item.classList.remove('is-open'); });
    item.addEventListener('focusout', (e) => { if (!item.contains(e.relatedTarget)) { set(false); item.classList.remove('is-closed'); } });
    if (link) link.addEventListener('keydown', (e) => { if (e.key === ' ' || e.key === 'Spacebar') { e.preventDefault(); set(!item.classList.contains('is-open')); if (item.classList.contains('is-open')) { const first = item.querySelector('.mf-nav__sub a'); if (first) first.focus(); } } });
  });
}
function fitNav(header) {
  const list = $('[data-mf-nav-list]', header);
  if (!list) return;
  list.classList.remove('is-tight', 'is-tighter');
  if (!isDesktopNav() || !list.clientWidth) return;
  const overflows = () => {
    const items = Array.from(list.children);
    const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
    const total = items.reduce((w, li) => w + li.getBoundingClientRect().width, 0) + gap * Math.max(0, items.length - 1);
    return total > list.clientWidth + 0.5;
  };
  if (overflows()) list.classList.add('is-tight');
  if (overflows()) list.classList.add('is-tighter');
  header.dataset.mfNavFit = list.classList.contains('is-tighter') ? 'tighter' : (list.classList.contains('is-tight') ? 'tight' : 'spec');
}
let headerBound = null;
function initHeader() {
  const section = $('.mf-header-section');
  const header = $('[data-mf-header]');
  if (!section || !header) return;
  const mode = header.dataset.mfSticky || 'compact';
  section.classList.toggle('is-sticky-none', mode === 'none');
  section.classList.remove('is-compact');
  const setVars = () => {
    const h = section.offsetHeight;
    document.documentElement.style.setProperty('--mf-header-offset', `${mode === 'none' ? 0 : h}px`);
    // Horizon only measures these when #header-component exists (its header section is disabled) — keep drawers/anchors right.
    document.body.style.setProperty('--header-height', `${h}px`);
    const group = $('#header-group');
    if (group) {
      let total = 0;
      for (const el of group.children) if (el instanceof HTMLElement) total += el.offsetHeight;
      document.body.style.setProperty('--header-group-height', `${total}px`);
    }
  };
  setVars();
  initNav(header);
  fitNav(header);
  if (headerBound === section) return;
  headerBound = section;
  new ResizeObserver(setVars).observe(section);
  window.addEventListener('resize', debounce(() => fitNav(header), 150));
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => fitNav(header));
  if (mode !== 'compact') return;
  let lastY = scrollTop(); let ticking = false;
  const setCompact = (v) => { if (section.classList.contains('is-compact') !== v) section.classList.toggle('is-compact', v); };
  const apply = () => {
    ticking = false;
    const y = Math.max(0, scrollTop());
    if (!isDesktopNav() || y <= 300) setCompact(false);
    else if (y - lastY > 4) setCompact(true);
    else if (lastY - y > 4) setCompact(false);
    lastY = y;
  };
  onScroll(() => { if (!ticking) { ticking = true; requestAnimationFrame(apply); } });
  header.addEventListener('focusin', () => setCompact(false));
  apply();
}

/* ---------------- auto-init for HTML swapped in later ----------------
 * Horizon 4.2.0 emits no generic "section rendered/morphed" event (grep theme-work/full/assets: morph.js,
 * section-renderer.js, section-hydration.js dispatch nothing; facets.js:135/192 fire 'shopify:search:update' /
 * 'shopify:collection:update' BEFORE the new HTML arrives). AJAX writes (filters, load more, recently viewed, drawer)
 * all end in DOM writes, so a debounced MutationObserver re-runs the (idempotent) initialisers on added subtrees. */
const AUTO_SEL = '[data-mf-hover-img], [data-mf-carousel], img.mf-fade-in, [data-mf-rotator], [data-mf-recent-add], img[data-mf-sizes-filter]';
function initAll(root) { initHoverImages(root); initCarousels(root); initFadeImages(root); scanBroken(root instanceof Element ? root : document); initRotators(root); trackRecent(root); applyFilterSizes(root instanceof Element ? root : document); }
function observeDom() {
  if (MF.observer) return;
  let pending = new Set(); let timer = 0;
  const flush = () => {
    timer = 0;
    const roots = Array.from(pending).filter((n) => n.isConnected); pending = new Set();
    roots.forEach((n) => initAll(n));
    if (roots.length) emit(MF.events.domUpdate, { roots });
  };
  MF.observer = new MutationObserver((records) => {
    for (const r of records) for (const n of r.addedNodes) {
      if (n.nodeType !== 1) continue;
      if (n.matches(AUTO_SEL) || n.querySelector(AUTO_SEL)) pending.add(n);
    }
    if (pending.size && !timer) timer = setTimeout(flush, 50);
  });
  MF.observer.observe(document.body, { childList: true, subtree: true });
}

/* ---------------- boot ---------------- */
function boot() {
  $$('[data-mf-offcanvas], [data-mf-search-panel]').forEach((el) => { if (!el.classList.contains('is-open')) { el.setAttribute('aria-hidden', 'true'); el.setAttribute('inert', ''); } });
  const cart = cartRoot(); if (cart && !cart.classList.contains('is-open')) { cart.setAttribute('aria-hidden', 'true'); cart.setAttribute('inert', ''); }
  initHeader();
  initAll(document);
  syncDesktopAccordions();
  restoreFilterState();
  // leaving the hamburger layout closes the mobile menu + search panel; crossing 769 re-syncs footer accordions / filter
  window.matchMedia(`(max-width: ${MF.bp.nav}px)`).addEventListener('change', (e) => { if (!e.matches) { closeOffcanvas('mf-mobile-menu'); closeSearch(); } });
  window.matchMedia(`(min-width: ${MF.bp.md}px)`).addEventListener('change', () => { syncDesktopAccordions(); if (!MF.mq.up('md')) setFilterOpen(false, false); else restoreFilterState(); });
  document.addEventListener('shopify:section:load', (e) => { initAll(e.target); if (e.target.querySelector && e.target.querySelector('[data-mf-header]')) { headerBound = null; initHeader(); } syncDesktopAccordions(); });
  observeDom();
  MF.ready = true;
  emit(MF.events.ready, { MF });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
